import { useCallback, useEffect, useState } from 'react';

import { enUS } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';

import { DateRangeV2 } from '../types';

/** Monday as week start (1 = Monday, 0 = Sunday) per European standard */
const WEEK_START_DAY = 1;

/** Years reachable in each direction via the month/year caption dropdowns */
const YEAR_DROPDOWN_RANGE = 20;

interface InlineCalendarProps {
  /** Current date range value */
  dates: DateRangeV2 | null;
  /** Callback when date range changes */
  onChange: (dates: DateRangeV2) => void;
  /** Callback when reset button is clicked (sets to today) */
  onReset: () => void;
}

/**
 * Inline calendar for custom date range selection.
 * Displayed directly in the dropdown content (not a submenu).
 */
export function InlineCalendar({ dates, onChange, onReset }: InlineCalendarProps) {
  const { t } = useTranslation();

  // Local state for range selection
  const [localRange, setLocalRange] = useState<{ from: Date | undefined; to: Date | undefined }>({
    from: dates?.startDate,
    to: dates?.endDate,
  });

  // Track selection state for range picking
  const [isSelectingEnd, setIsSelectingEnd] = useState(false);

  // Sync local state with props when dates change
  useEffect(() => {
    setLocalRange({
      from: dates?.startDate,
      to: dates?.endDate,
    });
  }, [dates?.startDate, dates?.endDate]);

  // Create locale config with Monday as first day
  const localeConfig = {
    ...enUS,
    options: {
      ...enUS.options,
      weekStartsOn: WEEK_START_DAY as 0 | 1 | 2 | 3 | 4 | 5 | 6,
    },
  };

  /**
   * Handles day click for range selection
   */
  const handleDayClick = useCallback(
    (date: Date) => {
      if (!isSelectingEnd || !localRange.from) {
        // Start new selection
        setLocalRange({ from: date, to: date });
        setIsSelectingEnd(true);
      } else {
        // Complete selection
        let from = localRange.from;
        let to = date;

        // Ensure from is before to
        if (to < from) {
          [from, to] = [to, from];
        }

        setLocalRange({ from, to });
        setIsSelectingEnd(false);

        // Emit change
        onChange({ startDate: from, endDate: to });
      }
    },
    [isSelectingEnd, localRange.from, onChange]
  );

  return (
    <div className="flex flex-col">
      <Calendar
        mode="range"
        selected={localRange}
        onDayClick={handleDayClick}
        defaultMonth={localRange.from}
        locale={localeConfig}
        weekStartsOn={WEEK_START_DAY}
        showOutsideDays
        fixedWeeks
        captionLayout="dropdown"
        startMonth={new Date(new Date().getFullYear() - YEAR_DROPDOWN_RANGE, 0)}
        endMonth={new Date(new Date().getFullYear() + YEAR_DROPDOWN_RANGE, 11)}
        className="w-full py-1 2xl:py-3"
        onSelect={range => {
          // Need to keep this method in order to keep date selector reset working
          if (!range?.from) return;
        }}
      />

      {/* Footer with clear and selected count */}
      <div className="flex items-center justify-between border-t px-3 py-2">
        <Button type="button" size="sm" onClick={onReset} className="h-8 px-3 text-sm">
          {t('common:dateRangePickerV2.reset')}
        </Button>
      </div>
    </div>
  );
}
